import { useEffect, useState } from "react";
import { api } from "./api";
import type { GuideList } from "../types";

/**
 * The selected org's guide collections, for the rail and the Guides page.
 * `undefined` while loading; an org without any (or a caller outside it — the API
 * 404s) resolves to an empty list, which is what keeps Guides off the rail.
 */
export function useGuides(org: string): GuideList | undefined {
  const [list, setList] = useState<GuideList | undefined>(undefined);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onChange = () => setTick((t) => t + 1);
    window.addEventListener(GUIDES_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(GUIDES_CHANGED_EVENT, onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // A refetch for the same org keeps its list on screen until the new one lands;
    // switching org clears it so the old org's guides never show under the new one.
    setList((prev) => (prev?.org === org ? prev : undefined));
    api
      .getGuides(org)
      .then((d) => {
        if (!cancelled) setList(d);
      })
      .catch(() => {
        if (!cancelled) setList({ org, collections: [], canEdit: false });
      });
    return () => {
      cancelled = true;
    };
  }, [org, tick]);
  return list;
}

/** Fired by the Guides page after it adds a page (the list has no SSE event). */
export const GUIDES_CHANGED_EVENT = "omni-hub:guides-changed";
export function notifyGuidesChanged(): void {
  window.dispatchEvent(new Event(GUIDES_CHANGED_EVENT));
}
